import { useState } from 'react'
import { createLesson, errorMessage, uploadAudio, uploadVideo } from '../../../services/admin'
import { faDigits } from '../../../lib/numerals'
import { LEVELS, LEVEL_FA } from '../constants'
import { dictionaryRefusal, pruneWordLinks } from '../dictionary'
import { createWithMedia, mediaKind } from '../lessonCreate'
import { WordLinkInputs } from './WordLinkInputs'

// ─── Create form ──────────────────────────────────────────────────────────────

/**
 * One step, one box (owner decision, 2026-10-13): a title, the exercise's one
 * English sentence (with its words linked to the dictionary as it is
 * written), one file, the level and the display order. Teaching is
 * monolingual, so there is no Persian title or translation; the slug is
 * derived on the server.
 *
 * The file is a video *or* a voice recording — one of the two, never both.
 * A video's sound track becomes the exercise's audio on the server; a voice
 * file is that audio itself. In the app the learner shadows whichever there
 * is: the video, or the audio alone.
 *
 * Two calls under one button: create, then upload the file
 * (`createWithMedia`). If the upload fails the lesson already exists, so the
 * form holds on to it and the button only retries the upload — pressing it
 * again never makes a second lesson.
 */
export function CreateForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ titleEn: '', textEn: '', wordLinks: [], level: 'beginner', displayOrder: 0 })
  const [file, setFile] = useState(null)
  // The lesson, once created, while its file still has to go up.
  const [created, setCreated] = useState(null)
  const [progress, setProgress] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  // A text change prunes any link whose word no longer matches its entry —
  // no fuzzy word-tracking, just drop what no longer lines up.
  const setText = textEn => setForm(f => ({ ...f, textEn, wordLinks: pruneWordLinks(f.wordLinks, textEn) }))

  async function submit(e) {
    e.preventDefault()
    if (!file) { setErr('ویدیو یا صدای تمرین را انتخاب کنید.'); return }
    if (!mediaKind(file)) { setErr('فایل باید ویدیو یا صدا باشد.'); return }
    setBusy(true); setErr(''); setProgress(null)
    let lesson = created
    try {
      onCreated(await createWithMedia(
        { created, form, file, onDraft: l => { lesson = l; setCreated(l) }, onProgress: setProgress },
        { createLesson, uploadVideo, uploadAudio },
      ))
    } catch (ex) {
      setErr(lesson
        ? `درس ساخته شد ولی فایل بارگذاری نشد: ${errorMessage(ex)}`
        : dictionaryRefusal(ex) ?? errorMessage(ex))
    } finally { setBusy(false); setProgress(null) }
  }

  const FL = 'mb-1 block text-xs font-medium text-slate-600'
  const FI = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500'
  const locked = created !== null

  const label = busy
    ? progress === null ? 'در حال ذخیره…' : `بارگذاری فایل… ${faDigits(Math.round(progress * 100))}٪`
    : locked ? 'بارگذاری دوبارهٔ فایل' : 'ایجاد درس'

  return (
    <form onSubmit={submit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <h2 className="text-lg font-bold text-slate-800">ایجاد درس</h2>

      <label className="block">
        <span className={FL}>عنوان درس *</span>
        <input required value={form.titleEn} className={FI} placeholder="Allergic" dir="ltr" disabled={locked}
          onChange={e => set('titleEn', e.target.value)} />
      </label>

      <div className="space-y-1.5">
        <label className="block">
          <span className={FL}>جمله (انگلیسی) *</span>
          <textarea required value={form.textEn} rows={2} className={`${FI} resize-none`} dir="ltr" disabled={locked}
            placeholder="I'm allergic to peanuts." onChange={e => setText(e.target.value)} />
        </label>
        {!locked && <WordLinkInputs textEn={form.textEn} links={form.wordLinks} onChange={next => set('wordLinks', next)} />}
      </div>

      <label className="block">
        <span className={FL}>ویدیو یا صدا *</span>
        <input required type="file" accept="video/*,audio/*" className={FI}
          onChange={e => setFile(e.target.files?.[0] ?? null)} />
        <span className="mt-1 block text-xs text-slate-400">
          یکی کافی است: ویدیو (صدای آن برای تمرین پخش می‌شود) یا فقط صدای معلم. کاربر با همان که هست تمرین می‌کند.
        </span>
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={FL}>سطح *</span>
          <select value={form.level} onChange={e => set('level', e.target.value)} className={FI} disabled={locked}>
            {LEVELS.map(l => <option key={l} value={l}>{LEVEL_FA[l]}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={FL}>ترتیب نمایش</span>
          <input type="number" min="0" step="1" value={form.displayOrder} className={FI} disabled={locked}
            onChange={e => set('displayOrder', e.target.value)} />
        </label>
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}

      <div className="flex gap-3 pt-1">
        <button type="submit" disabled={busy}
          className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
          {label}
        </button>
        <button type="button" disabled={busy} onClick={() => (created ? onCreated(created) : onCancel())}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          {created ? 'بعداً فایل می‌گذارم' : 'انصراف'}
        </button>
      </div>
    </form>
  )
}
